<template>
  <div
    class="twig-hu-color-picker"
    :class="{ light: isLightTheme }"
    :style="{ width: totalWidth + 'px' }"
  >
    <div class="color-set">
      <Saturation
        ref="saturation"
        :color="rgbString"
        :hsv="hsv"
        :size="hueHeight"
        @selectSaturation="selectSaturation"
        @startSelectingColor="startSelectingColor"
        @stopSelectingColor="stopSelectingColor"
      />
      <Hue
        ref="hue"
        :hsv="hsv"
        :width="hueWidth"
        :height="hueHeight"
        @selectHue="selectHue"
        @startSelectingColor="startSelectingColor"
        @stopSelectingColor="stopSelectingColor"
      />
    </div>
    <div
      :style="{ height: previewHeight + 'px' }"
      class="color-show flex items-center gap-2"
    >
      <Preview
        :key="previewWidth.toString()"
        :color="rgbaString"
        :width="previewWidth"
        :height="previewHeight"
      />
      <EyeDropper
        v-if="eyeDropperIsAvaliable"
        :inverse="inverse"
        @chooseColor="selectColor"
      />
      <Sucker
        v-if="!suckerHide"
        :sucker-canvas="suckerCanvas"
        :sucker-area="suckerArea"
        @openSucker="openSucker"
        @selectSucker="selectSucker"
      />
    </div>
    <Box
      name="hex"
      :color="modelHex"
      :inverse="inverse"
      @inputColor="inputHex"
    />
    <TLabel v-if="templateColors.length > 0" class="mb-[-8px]">
      {{ templateLabel }}
    </TLabel>
    <Colors
      v-if="templateColors.length > 0"
      :color="rgbaString"
      :colors-default="templateColors"
      :colors-history-key="colorsHistoryKey"
      @selectColor="selectColor"
    />
    <TLabel v-if="templateColors.length > 0" class="mb-[-8px] mt-1">
      {{ predefinedLabel }}
    </TLabel>
    <Colors
      data-testid="predefined-colors"
      :color="rgbaString"
      :colors-default="colorsDefault"
      :colors-history-key="colorsHistoryKey"
      @selectColor="selectColor"
    />
  </div>
</template>

<script>
import mixin from "./mixin";
import Saturation from "./Saturation.vue";
import Hue from "./Hue.vue";
import Preview from "./Preview.vue";
import Sucker from "./Sucker.vue";
import Box from "./Box.vue";
import Colors from "./Colors.vue";
import EyeDropper from "./Eyedropper.vue";
import TLabel from "../../TLabel/TLabel.vue";
export default {
  mixins: [mixin],
  props: {
    color: {
      type: String,
      default: "#000000",
    },
    theme: {
      type: String,
      default: "dark",
    },
    suckerHide: {
      type: Boolean,
      default: true,
    },
    suckerCanvas: {
      type: null, // HTMLCanvasElement
      default: null,
    },
    suckerArea: {
      type: Array,
      default: () => [],
    },
    colorsDefault: {
      type: Array,
      default: () => [
        "#000000",
        "#FFFFFF",
        "#FF1900",
        "#F47365",
        "#FFB243",
        "#FFE623",
        "#6EFF2A",
        "#1BC7B1",
        "#00BEFF",
        "#2E81FF",
        "#5D61FF",
        "#FF89CF",
        "#FC3CAD",
        "#BF3DCE",
        "#8E00A7",
        "rgba(0,0,0,0)",
      ],
    },
    colorsHistoryKey: {
      type: String,
      default: "vue-colorpicker-history",
    },
    templateColors: {
      type: Array,
      default: () => [],
    },
    templateLabel: {
      type: String,
      reguired: true,
    },
    predefinedLabel: {
      type: String,
      reguired: true,
    },
    inverse: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Saturation,
    Hue,
    Preview,
    Sucker,
    Box,
    Colors,
    EyeDropper,
    TLabel,
  },
  data() {
    return {
      hueWidth: 15,
      hueHeight: 152,
      previewHeight: 30,
      modelRgba: "",
      modelHex: "",
      r: 0,
      g: 0,
      b: 0,
      a: 1,
      h: 0,
      s: 0,
      v: 0,
      eyeDropperIsAvaliable: true,
    };
  },
  computed: {
    isLightTheme() {
      return this.theme === "light";
    },
    totalWidth() {
      return this.hueHeight + (this.hueWidth + 8) * 2;
    },
    previewWidth() {
      return (
        this.totalWidth -
        (this.suckerHide ? 0 : this.previewHeight) -
        (this.eyeDropperIsAvaliable ? 52 : 20)
      );
    },
    rgba() {
      return {
        r: this.r,
        g: this.g,
        b: this.b,
        a: this.a,
      };
    },
    hsv() {
      return {
        h: this.h,
        s: this.s,
        v: this.v,
      };
    },
    rgbString() {
      return `rgb(${this.r}, ${this.g}, ${this.b})`;
    },
    rgbaStringShort() {
      return `${this.r}, ${this.g}, ${this.b}, ${this.a}`;
    },
    rgbaString() {
      return `rgba(${this.rgbaStringShort})`;
    },
    hexString() {
      return this.rgb2hex(this.rgba, true);
    },
  },
  methods: {
    // Helper metoda pro normalizaci "transparent"
    normalizeColor(color) {
      if (
        color &&
        typeof color === "string" &&
        color.toLowerCase() === "transparent"
      ) {
        return "rgba(0, 0, 0, 0)";
      }
      return color;
    },

    selectSaturation(color) {
      this.a = 1;
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, h, s, v });
      this.setText();
    },
    selectHue(color) {
      this.a = 1;
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, h, s, v });
      this.setText();
      this.$nextTick(() => {
        if (this.$refs.saturation) {
          this.$refs.saturation.renderColor();
          this.$refs.saturation.renderSlide();
        }
      });
    },
    selectAlpha(a) {
      this.a = a;
      this.setText();
    },
    inputHex(color) {
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, a, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, a, h, s, v });

      // Zachováváme originální "transparent" pro zobrazení
      this.modelHex = color;
      this.modelRgba =
        color && color.toLowerCase() === "transparent"
          ? "transparent"
          : this.rgbaStringShort;

      this.$nextTick(() => {
        if (this.$refs.saturation && this.$refs.hue) {
          this.$refs.saturation.renderColor();
          this.$refs.saturation.renderSlide();
          this.$refs.hue.renderSlide();
        }
      });
    },
    inputRgba(color) {
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, a, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.modelHex = this.hexString;
      this.modelRgba = color;
      this.$nextTick(() => {
        if (this.$refs.saturation && this.$refs.hue) {
          this.$refs.saturation.renderColor();
          this.$refs.saturation.renderSlide();
          this.$refs.hue.renderSlide();
        }
      });
    },
    setText() {
      if (this.a === 0) {
        this.modelHex = "transparent";
        this.modelRgba = "transparent";
      } else {
        this.modelHex = this.hexString;
        this.modelRgba = this.rgbaStringShort;
      }
    },
    openSucker(isOpen) {
      this.$emit("openSucker", isOpen);
    },
    selectSucker(color) {
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, a, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.setText();
      this.$nextTick(() => {
        if (this.$refs.saturation && this.$refs.hue) {
          this.$refs.saturation.renderColor();
          this.$refs.saturation.renderSlide();
          this.$refs.hue.renderSlide();
        }
      });
    },
    selectColor(color) {
      const normalizedColor = this.normalizeColor(color);
      const { r, g, b, a, h, s, v } = this.setColorValue(normalizedColor);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.setText();
      this.$nextTick(() => {
        if (this.$refs.saturation && this.$refs.hue) {
          this.$refs.saturation.renderColor();
          this.$refs.saturation.renderSlide();
          this.$refs.hue.renderSlide();
        }
      });
    },
    startSelectingColor() {
      this.$emit("startSelectingColor");
    },
    stopSelectingColor() {
      this.$emit("stopSelectingColor");
    },
  },

  created() {
    const normalizedColor = this.normalizeColor(this.color);
    Object.assign(this, this.setColorValue(normalizedColor));
    this.setText();

    // 避免初始化时，也会触发changeColor事件
    this.$watch("rgba", () => {
      this.$emit("changeColor", {
        rgba: this.rgba,
        hsv: this.hsv,
        hex: this.modelHex,
      });
    });
  },

  mounted() {
    if (!window.EyeDropper) {
      this.eyeDropperIsAvaliable = false;
    }
  },
};
</script>

<style lang="scss">
.twig-hu-color-picker {
  padding: 10px;
  background: #1d2024;
  border-radius: 6px;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  z-index: 1;
  &.light {
    background: #f7f8f9;
    .color-show {
      .sucker {
        background: #eceef0;
      }
    }
    .color-type {
      .name {
        background: #e7e8e9;
      }
      .value {
        color: #666;
        background: #eceef0;
      }
    }
    .colors.history {
      border-top: 1px solid #eee;
    }
  }
  canvas {
    vertical-align: top;
  }
  .color-set {
    display: flex;
  }
  .color-show {
    margin-top: 8px;
    display: flex;
  }
}
</style>
